(() => {
  if (customElements.get('business-account-panel')) return;

  const DEFAULT_API_URL = 'https://business-hub-app.vytedev.com/api/application-apply';

  function isValidApiUrl(url) {
    try {
      const parsed = new URL(url);
      return (
        parsed.protocol === 'https:' &&
        /\/api\/application-apply\/?$/i.test(parsed.pathname)
      );
    } catch {
      return false;
    }
  }

  function resolveApiUrl(raw) {
    let value = String(raw || '').trim();
    if (!value) return DEFAULT_API_URL;

    if (value.startsWith('/') && !value.startsWith('//') && value.includes('.')) {
      value = value.replace(/^\/+/, '');
    }

    value = value.replace(/\/+$/, '');
    value = value.replace(/\/api\/application-apply(\/api\/application-apply)+$/i, '/api/application-apply');
    if (!/\/api\/application-apply$/i.test(value)) {
      value = `${value.replace(/\/api\/application-apply$/i, '')}/api/application-apply`;
    }

    if (/^https?:\/\//i.test(value)) {
      return ensureExternalApiUrl(
        isValidApiUrl(value) ? value : DEFAULT_API_URL,
      );
    }
    if (value.startsWith('//')) {
      const protocolRelative = `https:${value}`;
      return ensureExternalApiUrl(
        isValidApiUrl(protocolRelative) ? protocolRelative : DEFAULT_API_URL,
      );
    }

    const hostPath = value.replace(/^\/+/, '');
    if (!hostPath || hostPath === 'api/application-apply') return DEFAULT_API_URL;

    const withHttps = `https://${hostPath}`;
    return ensureExternalApiUrl(
      isValidApiUrl(withHttps) ? withHttps : DEFAULT_API_URL,
    );
  }

  function isStorefrontMisdirectedUrl(url) {
    try {
      if (typeof window === 'undefined' || !window.location?.hostname) return false;
      const parsed = new URL(url, window.location.origin);
      const onStorefront =
        /\.myshopify\.com$/i.test(window.location.hostname) ||
        /\.shopifypreview\.com$/i.test(window.location.hostname) ||
        !window.location.hostname.includes('vytedev.com');

      return onStorefront && parsed.hostname === window.location.hostname;
    } catch {
      return false;
    }
  }

  function ensureExternalApiUrl(url) {
    try {
      const parsed = new URL(url, window.location?.origin || undefined);
      if (isStorefrontMisdirectedUrl(parsed.href)) {
        return DEFAULT_API_URL;
      }
      if (
        typeof window !== 'undefined' &&
        window.location?.origin &&
        parsed.origin === window.location.origin
      ) {
        return DEFAULT_API_URL;
      }
      return isValidApiUrl(parsed.href) ? parsed.href : DEFAULT_API_URL;
    } catch {
      return DEFAULT_API_URL;
    }
  }

  class BusinessAccountPanel extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.group = this.dataset.group || '';
      this.apiUrl = resolveApiUrl(this.dataset.apiUrl);
      this.overlay = this.querySelector('[data-biz-account-overlay]');
      this.closeBtn = this.querySelector('[data-biz-account-close]');
      this.form = this.querySelector('.biz-account-panel__form');
      this.success = this.querySelector('[data-biz-account-success]');
      this.error = this.querySelector('[data-biz-account-error]');

      this._onTrigger = this._onTrigger.bind(this);
      this._onOverlay = () => this.setOpen(false);
      this._onClose = () => this.setOpen(false);
      this._onKeydown = (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.setOpen(false);
      };
      this._onSubmit = this._onSubmit.bind(this);

      document.addEventListener('click', this._onTrigger);
      if (this.overlay) this.overlay.addEventListener('click', this._onOverlay);
      if (this.closeBtn) this.closeBtn.addEventListener('click', this._onClose);
      document.addEventListener('keydown', this._onKeydown);
      if (this.form) this.form.addEventListener('submit', this._onSubmit);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onTrigger);
      document.removeEventListener('keydown', this._onKeydown);
      if (this.classList.contains('is-open')) document.body.classList.remove('biz-account-panel-open');
    }

    _onTrigger(e) {
      const btn = e.target.closest('[data-business-account-open]');
      if (!btn) return;
      const target = btn.getAttribute('data-business-account-target');
      if (target && target !== this.group) return;
      e.preventDefault();
      this.setOpen(true);
    }

    setOpen(open) {
      if (open) {
        if (this.form) {
          this.form.hidden = false;
          this.form.reset();
          this._clearFieldErrors();
        }
        if (this.success) this.success.hidden = true;
        if (this.error) this.error.hidden = true;
        if (this.overlay) this.overlay.hidden = false;
        const submitBtn = this.form?.querySelector('[type="submit"]');
        if (submitBtn) submitBtn.disabled = false;
      } else if (this.overlay) {
        this.overlay.hidden = true;
      }

      this.classList.toggle('is-open', open);
      document.body.classList.toggle('biz-account-panel-open', open);
    }

    _clearFieldErrors() {
      this.form?.querySelectorAll('.biz-account-panel__input--error').forEach((el) => {
        el.classList.remove('biz-account-panel__input--error');
      });
    }

    _value(name) {
      const field = this.form?.querySelector(`[name="${name}"]`);
      if (!field) return '';
      if (field.type === 'radio') {
        const checked = this.form.querySelector(`[name="${name}"]:checked`);
        return checked ? checked.value.trim() : '';
      }
      return field.value.trim();
    }

    _interestValues() {
      return [...this.form.querySelectorAll('[name="productInterests"]:checked')].map((el) => el.value);
    }

    _buildPayload() {
      return {
        businessName: this._value('businessName'),
        businessRegistrationNumber: this._value('businessRegistrationNumber'),
        industry: this._value('industry'),
        website: this._value('website') || undefined,
        contactName: this._value('contactName'),
        jobTitle: this._value('jobTitle') || undefined,
        email: this._value('email'),
        phone: this._value('phone'),
        accountsPayableEmail: this._value('accountsPayableEmail') || undefined,
        purchaseOrdersRequired: this._value('purchaseOrdersRequired') || undefined,
        companySize: this._value('companySize') || undefined,
        estimatedAnnualSpend: this._value('estimatedAnnualSpend') || undefined,
        businessAddressStreet: this._value('businessAddressStreet'),
        businessAddressSuburb: this._value('businessAddressSuburb'),
        businessAddressState: this._value('businessAddressState'),
        businessAddressPostcode: this._value('businessAddressPostcode'),
        productInterests: this._interestValues(),
        shopDomain: this._value('shopDomain'),
        submissionSource: 'storefront_form',
      };
    }

    _showFieldErrors(fieldErrors) {
      if (!fieldErrors || !this.form) return;
      Object.keys(fieldErrors).forEach((key) => {
        const field = this.form.querySelector(`[name="${key}"]`);
        if (field) field.classList.add('biz-account-panel__input--error');
      });
    }

    _showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
      if (this.success) this.success.hidden = true;
    }

    async _onSubmit(e) {
      e.preventDefault();
      if (!this.form) return;

      this._clearFieldErrors();
      if (this.error) this.error.hidden = true;

      const submitBtn = this.form.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;

      try {
        const requestUrl = ensureExternalApiUrl(
          resolveApiUrl(this.dataset.apiUrl || this.apiUrl),
        );
        const response = await fetch(requestUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(this._buildPayload()),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          this._showFieldErrors(data.fieldErrors);
          const message =
            response.status === 404
              ? 'Application API was not found. Check the Business Hub API URL in theme settings.'
              : data.error || 'Unable to submit application. Please try again.';
          this._showError(message);
          if (submitBtn) submitBtn.disabled = false;
          return;
        }

        if (this.success) this.success.hidden = false;
        if (window.ModalToast) window.ModalToast.showFrom(this.success);
        this.form.hidden = true;
        if (submitBtn) submitBtn.disabled = false;

        window.setTimeout(() => this.setOpen(false), 2400);
      } catch {
        this._showError('Unable to submit application. Please try again.');
        if (submitBtn) submitBtn.disabled = false;
      }
    }
  }

  customElements.define('business-account-panel', BusinessAccountPanel);
})();
